import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card, Badge, Modal, Select } from "../components/ui";

const TIPO_UTENZA_OPTIONS = {
  100000000: {
    label: "Energia elettrica",
    icon: "⚡",
    color: "text-yellow-600",
  },
  100000001: { label: "Gas naturale", icon: "🔥", color: "text-orange-600" },
  100000002: { label: "Acqua", icon: "💧", color: "text-blue-600" },
  100000003: { label: "Teleriscaldamento", icon: "🌡️", color: "text-red-600" },
  100000004: {
    label: "Telecomunicazioni",
    icon: "📡",
    color: "text-purple-600",
  },
  100000005: { label: "Rifiuti", icon: "♻️", color: "text-green-600" },
  100000007: {
    label: "Rifiuti non speciali",
    icon: "♻️",
    color: "text-green-600",
  },
  100000008: {
    label: "Rifiuti speciali",
    icon: "☣️",
    color: "text-amber-700",
  },
  100000006: { label: "Altro", icon: "📦", color: "text-gray-600" },
};

const STATO_PAGAMENTO_OPTIONS = {
  100000000: { label: "Da pagare", variant: "warning" },
  100000001: { label: "Pagata", variant: "success" },
  100000002: { label: "Scaduta", variant: "danger" },
  100000003: { label: "In contestazione", variant: "info" },
};

function DettaglioConsumoPage() {
  const { id } = useParams();
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [consumo, setConsumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatoModal, setShowStatoModal] = useState(false);
  const [nuovoStato, setNuovoStato] = useState("");

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const canEdit = isAdmin || isRspp;
  const canDelete = isAdmin;

  useEffect(() => {
    const fetchConsumo = async () => {
      try {
        const data = await apiRequest(`/consumi-utenze/${id}`);
        setConsumo(data);
      } catch (error) {
        console.error("Error fetching consumo:", error);
        showToast("Errore nel caricamento del consumo", "error");
        navigate("/consumi-utenze");
      } finally {
        setLoading(false);
      }
    };

    fetchConsumo();
  }, [id, apiRequest, showToast, navigate]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("it-IT", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("it-IT");
  };

  const hasDetailedCosts = (c) =>
    c.nomeOfferta ||
    c.prezzoUnitario != null ||
    c.quotaFissa != null ||
    c.spesaTrasporto != null ||
    c.accise != null ||
    c.iva != null;

  const handleStatoChange = async () => {
    if (!nuovoStato) return;
    setSaving(true);
    try {
      await apiRequest(`/consumi-utenze/${id}/stato`, {
        method: "PATCH",
        body: JSON.stringify({ statoPagamento: parseInt(nuovoStato) }),
      });
      setConsumo((prev) => ({
        ...prev,
        statoPagamento: parseInt(nuovoStato),
        statoPagamentoLabel: STATO_PAGAMENTO_OPTIONS[nuovoStato]?.label,
      }));
      showToast("Stato pagamento aggiornato", "success");
      setShowStatoModal(false);
    } catch (error) {
      console.error("Error updating stato:", error);
      showToast("Errore nell'aggiornamento dello stato", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await apiRequest(`/consumi-utenze/${id}`, { method: "DELETE" });
      showToast("Bolletta eliminata", "success");
      navigate("/consumi-utenze");
    } catch (error) {
      console.error("Error deleting consumo:", error);
      showToast("Errore nell'eliminazione", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!consumo) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Bolletta non trovata</p>
        <Button className="mt-4" onClick={() => navigate("/consumi-utenze")}>
          Torna alla lista
        </Button>
      </div>
    );
  }

  const tipo = TIPO_UTENZA_OPTIONS[consumo.tipoutenza];
  const stato = STATO_PAGAMENTO_OPTIONS[consumo.statoPagamento];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Link to="/consumi-utenze" className="hover:text-blue-600">
          Bollette e Consumi
        </Link>
        <span>/</span>
        <span className="text-gray-900">{consumo.nome}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className={`text-2xl`}>{tipo?.icon || "📦"}</span>
            <h1 className="text-2xl font-bold text-gray-900">{consumo.nome}</h1>
          </div>
          <div className="flex items-center gap-3 mt-2">
            {stato && <Badge variant={stato.variant}>{stato.label}</Badge>}
            {consumo.origineAutomatica ? (
              <Badge variant="info">Origine automatica</Badge>
            ) : (
              <Badge variant="default">Inserimento manuale</Badge>
            )}
            {consumo.confidenzaEstrazione !== null &&
              consumo.confidenzaEstrazione !== undefined && (
                <Badge
                  variant={
                    consumo.confidenzaEstrazione >= 70
                      ? "success"
                      : consumo.confidenzaEstrazione >= 40
                        ? "warning"
                        : "danger"
                  }
                >
                  Confidenza: {consumo.confidenzaEstrazione}%
                </Badge>
              )}
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setNuovoStato(String(consumo.statoPagamento));
                  setShowStatoModal(true);
                }}
              >
                Cambia Stato
              </Button>
              <Button
                onClick={() => navigate(`/consumi-utenze/${id}/modifica`)}
              >
                Modifica
              </Button>
            </>
          )}
          {canDelete && (
            <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
              Elimina
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Importi */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Dettaglio Importi</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Importo Totale</p>
                <p className="text-xl font-bold text-blue-700">
                  {formatCurrency(consumo.importoTotale)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Materia Prima</p>
                <p className="text-lg font-semibold">
                  {formatCurrency(consumo.importoEnergia)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Rete/Trasporto</p>
                <p className="text-lg font-semibold">
                  {formatCurrency(consumo.importoRete)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Imposte</p>
                <p className="text-lg font-semibold">
                  {formatCurrency(consumo.importoImposte)}
                </p>
              </div>
            </div>
          </Card>

          {/* Dettaglio Costi (from PDF extraction) */}
          {hasDetailedCosts(consumo) && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Dettaglio Costi</h2>

              {/* Offerta */}
              {consumo.nomeOfferta && (
                <div className="mb-4 p-3 bg-indigo-50 rounded-lg">
                  <p className="text-sm text-gray-500">Offerta</p>
                  <p className="font-semibold text-indigo-700">
                    {consumo.nomeOfferta}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Prezzo unitario */}
                {consumo.prezzoUnitario != null && (
                  <div>
                    <p className="text-sm text-gray-500">Prezzo Unitario</p>
                    <p className="font-semibold">
                      {formatNumber(consumo.prezzoUnitario, 6)} €/
                      {consumo.tipoutenza === 100000001 ? "Smc" : "kWh"}
                    </p>
                  </div>
                )}

                {/* Potenza impegnata (luce) */}
                {consumo.potenzaImpegnata != null && (
                  <div>
                    <p className="text-sm text-gray-500">Potenza Impegnata</p>
                    <p className="font-semibold">
                      {formatNumber(consumo.potenzaImpegnata, 1)} kW
                    </p>
                  </div>
                )}

                {/* Quota fissa */}
                {consumo.quotaFissa != null && (
                  <div>
                    <p className="text-sm text-gray-500">Quota Fissa</p>
                    <p className="font-semibold">
                      {formatCurrency(consumo.quotaFissa)}
                    </p>
                  </div>
                )}

                {/* Quota potenza */}
                {consumo.quotaPotenza != null && (
                  <div>
                    <p className="text-sm text-gray-500">Quota Potenza</p>
                    <p className="font-semibold">
                      {formatCurrency(consumo.quotaPotenza)}
                    </p>
                  </div>
                )}

                {/* Spesa trasporto */}
                {consumo.spesaTrasporto != null && (
                  <div>
                    <p className="text-sm text-gray-500">Spesa Trasporto</p>
                    <p className="font-semibold">
                      {formatCurrency(consumo.spesaTrasporto)}
                    </p>
                  </div>
                )}

                {/* Spesa oneri */}
                {consumo.spesaOneri != null && (
                  <div>
                    <p className="text-sm text-gray-500">Oneri di Sistema</p>
                    <p className="font-semibold">
                      {formatCurrency(consumo.spesaOneri)}
                    </p>
                  </div>
                )}

                {/* Accise */}
                {consumo.accise != null && (
                  <div>
                    <p className="text-sm text-gray-500">Accise</p>
                    <p className="font-semibold">
                      {formatCurrency(consumo.accise)}
                    </p>
                  </div>
                )}

                {/* IVA */}
                {consumo.iva != null && (
                  <div>
                    <p className="text-sm text-gray-500">IVA</p>
                    <p className="font-semibold">
                      {formatCurrency(consumo.iva)}
                    </p>
                  </div>
                )}
              </div>

              {/* Fasce orarie F1/F2/F3 (luce) */}
              {(consumo.consumoF1 != null || consumo.prezzoF1 != null) && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    Fasce Orarie
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500">
                          <th className="pb-2">Fascia</th>
                          <th className="pb-2 text-right">Consumo (kWh)</th>
                          <th className="pb-2 text-right">Prezzo (€/kWh)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {["F1", "F2", "F3"].map((fascia) => {
                          const consumoF = consumo[`consumo${fascia}`];
                          const prezzoF = consumo[`prezzo${fascia}`];
                          if (consumoF == null && prezzoF == null) return null;
                          return (
                            <tr
                              key={fascia}
                              className="border-t border-gray-100"
                            >
                              <td className="py-2 font-medium">{fascia}</td>
                              <td className="py-2 text-right">
                                {consumoF != null
                                  ? formatNumber(consumoF, 0)
                                  : "-"}
                              </td>
                              <td className="py-2 text-right">
                                {prezzoF != null
                                  ? formatNumber(prezzoF, 6)
                                  : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {consumo.dispacciamento != null && (
                    <p className="text-xs text-gray-500 mt-2">
                      Dispacciamento: {formatNumber(consumo.dispacciamento, 6)}{" "}
                      €/kWh | Sbilanciamento:{" "}
                      {formatNumber(consumo.sbilanciamento, 6)} €/kWh
                    </p>
                  )}
                </div>
              )}

              {/* Gas: PSV e Spread */}
              {(consumo.indicePsv != null || consumo.spreadAlfa != null) && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    Indici Gas
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {consumo.indicePsv != null && (
                      <div>
                        <p className="text-sm text-gray-500">Indice PSV</p>
                        <p className="font-semibold">
                          {formatNumber(consumo.indicePsv, 6)} €/Smc
                        </p>
                      </div>
                    )}
                    {consumo.spreadAlfa != null && (
                      <div>
                        <p className="text-sm text-gray-500">Spread / Alfa</p>
                        <p className="font-semibold">
                          {formatNumber(consumo.spreadAlfa, 6)} €/Smc
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Consumi */}
          {(consumo.consumoQuantita || consumo.periodoInizio) && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Dati Consumo</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {consumo.consumoQuantita !== null && (
                  <div>
                    <p className="text-sm text-gray-500">Consumo</p>
                    <p className="text-lg font-semibold">
                      {new Intl.NumberFormat("it-IT").format(
                        consumo.consumoQuantita,
                      )}{" "}
                      {consumo.consumoUnita || ""}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Periodo</p>
                  <p className="text-lg font-semibold">
                    {formatDate(consumo.periodoInizio)} -{" "}
                    {formatDate(consumo.periodoFine)}
                  </p>
                </div>
                {consumo.pod && (
                  <div>
                    <p className="text-sm text-gray-500">POD/PDR</p>
                    <p className="text-lg font-mono font-semibold">
                      {consumo.pod}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Fattura */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Dati Fattura</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {consumo.numeroFattura && (
                <div>
                  <p className="text-sm text-gray-500">Numero Fattura</p>
                  <p className="font-medium">{consumo.numeroFattura}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Data Emissione</p>
                <p className="font-medium">
                  {formatDate(consumo.dataEmissione)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Data Scadenza</p>
                <p className="font-medium">
                  {formatDate(consumo.dataScadenza)}
                </p>
              </div>
            </div>
          </Card>

          {/* Note */}
          {consumo.note && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-2">Note</h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {consumo.note}
              </p>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Collegamento */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Collegamento</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Tipo Utenza</p>
                <p className={`font-medium ${tipo?.color || ""}`}>
                  {tipo?.icon} {tipo?.label || "N/D"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">RSA</p>
                {consumo.rsaId ? (
                  <Link
                    to={`/rsa/${consumo.rsaId}`}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {consumo.rsaNome}
                  </Link>
                ) : (
                  <p className="text-gray-400">-</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Immobile</p>
                {consumo.immobileId ? (
                  <Link
                    to={`/immobili/${consumo.immobileId}`}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {consumo.immobileNome}
                  </Link>
                ) : (
                  <p className="text-gray-400">-</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Contatore</p>
                {consumo.contatoreId ? (
                  <p className="font-medium">
                    {consumo.contatoreNome}
                    {consumo.contatoreCodice && (
                      <span className="text-gray-500 text-sm ml-1">
                        ({consumo.contatoreCodice})
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-gray-400">-</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Fornitore</p>
                {consumo.fornitoreId ? (
                  <Link
                    to={`/fornitori/${consumo.fornitoreId}`}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {consumo.fornitoreNome}
                  </Link>
                ) : (
                  <p className="text-gray-400">-</p>
                )}
              </div>
            </div>
          </Card>

          {/* Documento */}
          {consumo.documentoUrl && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Documento</h2>
              <a
                href={consumo.documentoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 hover:underline"
              >
                📄 Apri bolletta PDF
              </a>
            </Card>
          )}
        </div>
      </div>

      {/* Modal Stato Pagamento */}
      <Modal
        isOpen={showStatoModal}
        onClose={() => setShowStatoModal(false)}
        title="Cambia Stato Pagamento"
      >
        <div className="space-y-4">
          <Select
            label="Nuovo stato"
            value={nuovoStato}
            onChange={(e) => setNuovoStato(e.target.value)}
            options={Object.entries(STATO_PAGAMENTO_OPTIONS).map(
              ([value, opt]) => ({
                value,
                label: opt.label,
              }),
            )}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowStatoModal(false)}>
              Annulla
            </Button>
            <Button onClick={handleStatoChange} disabled={saving}>
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Elimina */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Conferma Eliminazione"
      >
        <div className="space-y-4">
          <p>
            Sei sicuro di voler eliminare la bolletta{" "}
            <strong>{consumo.nome}</strong>?
          </p>
          <p className="text-red-600 text-sm">
            Questa azione non può essere annullata.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Annulla
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={saving}>
              {saving ? "Eliminazione..." : "Elimina"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default DettaglioConsumoPage;
